import * as GridUtils from "./gridUtils";
import { ColDef } from "ag-grid-enterprise";
import InputDatetimeCellEditor from "../components/grid/InputDatetimeCellEditor";
import { GetPfmLayout } from "../models/GetPfmLayout";
import MergedChildCellRenderer from "../components/grid/MergedChildCellRenderer";
import IconHeaderComponent from "../components/grid/IconHeaderComponent";
import { Mark } from "../models/Po";

// 先頭列は必ずチェックボックスを付与する（ロジック自動で付与されるので定義内に入れない）
export const CHECK_BOX_DEF = {
  headerName: "",
  field: "chkrow",
  checkboxSelection: true,
  headerCheckboxSelection: true,
  width: 50,
  comparator: "checkboxComparator",
  sortable: true,
};

// 内部処理の都合でソート用カラムを追加（ロジック自動で付与されるので定義内に入れない）
export const SORT_DEF = {
  headerName: "",
  field: "sort_order",
  width: 50,
  hide: true,
  sortable: true,
};

// marks 列の cellRenderer
const marksCellRenderer = (params: any) => {
  try {
    const marks: Mark[] = Array.isArray(params.value) ? params.value : [];
    if (!marks || marks.length === 0) {
      return "";
    }

    const productTypeMap: { [key: string]: string } = {
      LaminatedGlass: "合",
      TemperedGlass: "強",
      InsulatedGlass: "複",
      Others: "他",
    };

    const posNameMap: { [key: string]: string } = {
      右上: "↗︎",
      右下: "↘︎",
      左上: "↖︎",
      左下: "↙︎",
      中央上: "↑",
      中央下: "↓",
    };

    const replacePosName = (posName: string = ""): string => {
      if (typeof posName !== "string" || !posName) return "";
      const keys = Object.keys(posNameMap).sort((a, b) => b.length - a.length);
      for (const key of keys) {
        if (posName.startsWith(key)) {
          const symbol = posNameMap[key];
          return `${symbol}${posName}`;
        }
      }
      return posName;
    };

    return marks
      .slice(0, 10)
      .map((mark) => {
        if (!mark) return "";

        const productType =
          typeof mark.product_type === "string" ? mark.product_type : "";
        const productTypeSymbol =
          productTypeMap[productType] || productType || "";

        const posName =
          typeof mark.mark_pos_name === "string" ? mark.mark_pos_name : "";
        const posSymbol = replacePosName(posName);

        const isRightDown = posName.startsWith("右下");

        const displayPosSymbol = isRightDown ? posSymbol : `!!${posSymbol}!!`;

        const markName =
          typeof mark.mark_name === "string" ? mark.mark_name : "";

        return `${productTypeSymbol} ${markName} (${displayPosSymbol})`;
      })
      .filter((item) => item !== "")
      .join(", ");
  } catch (error) {
    console.error("Error in marks cellRenderer:", error);
    return "";
  }
};

const dateSettings: ColDef = {
  width: 110,
  sortable: true,
  floatingFilter: true,
  valueFormatter: GridUtils.formatDateWithWeekday,
  filter: "agDateColumnFilter",
  filterParams: {
    comparator: GridUtils.dateComparator,
    inRangeInclusive: true,
    browserDatePicker: true,
  },
};

const baseColumns = {
  proc_status: {
    headerName: "status",
    field: "proc_status",
    width: 70,
    valueFormatter: GridUtils.procStatusFormatter,
    cellStyle: GridUtils.procStatusCellSyle,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fa fa-tasks", "fa fa-sort"],
      iconColor: "#1773A1",
    },
  },
  ready: {
    headerName: "R",
    field: "ready",
    width: 60,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#49BEAA", "white"),
    filter: "agSetColumnFilter",
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-check", "fa fa-sort"],
      iconColor: "#1773A1",
    },
  },
  schedule_start_time: {
    headerName: "加工予定開始時刻",
    field: "schedule_start_time",
    width: 180,
    sortable: true,
    filter: "agTextColumnFilter",
    valueFormatter: (params: any) => {
      if (!params.value) return "";
      const date = new Date(params.value);
      const year = date.getFullYear().toString().slice(-2);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    },
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fa fa-clock"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
    hide: false,
  },
  urgency_level: {
    headerName: "急",
    field: "urgency_level",
    width: 70,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#FF33666", "black"),
    filter: "agSetColumnFilter",
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fa fa-ambulance"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
    hide: false,
  },
  sheet_type: {
    headerName: "板種",
    field: "sheet_type",
    width: 110,
    filter: "agSetColumnFilter",
    // headerComponent: IconHeaderComponent,
    // headerComponentParams: {
    //   iconClasses: ["fa fa-layer-group"],
    //   iconColor: "#1773A1",
    //   showHeaderName: true,
    // },
  },
  remaining_procs: {
    headerName: "残工程",
    field: "mach_abbr_names",
    width: 180,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-spinner"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  following_mach_name: {
    headerName: "次機械",
    field: "following_mach_name",
    width: 120,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-spinner"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  po_width: {
    headerName: "w",
    field: "po_width",
    width: 80,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-arrows-h"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  po_height: {
    headerName: "h",
    field: "po_height",
    width: 80,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-arrows-v"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  proc_width: {
    headerName: "pW",
    field: "proc_width",
    width: 90,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-arrows-h"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  proc_height: {
    headerName: "pH",
    field: "proc_height",
    width: 90,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-arrows-v"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  current_qty: {
    headerName: "現数",
    field: "current_qty",
    width: 80,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-calculator"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  mach_name: {
    headerName: "機械",
    field: "mach_name",
    width: 100,
    filter: "agSetColumnFilter",
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-cogs"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  destin_name: {
    headerName: "送り先名",
    field: "destin_name",
    filter: "agTextColumnFilter",
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-map-marker-alt"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  customer_name: {
    headerName: "得意先名",
    field: "customer_name",
    sortable: true,
    filter: "agTextColumnFilter",
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-user-tie"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  project_name: {
    headerName: "物件名",
    field: "project_name",
    width: 160,
    filter: "agTextColumnFilter",
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-project-diagram"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  completion_due_date: {
    headerName: "送日",
    field: "completion_due_date",
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["far fa-calendar", "fas fa-truck"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  ship_date: {
    headerName: "出日",
    field: "ship_date",
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["far fa-calendar", "fas fa-truck"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  arrival_date: {
    headerName: "着日",
    field: "arrival_date",
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["far fa-calendar", "fas fa-map-marker-alt"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  merged_children: {
    headerName: "ダミー進捗",
    field: "merged_children",
    width: 140,
    valueFormatter: (params: any) => {
      if (params.value && params.value.length > 0) {
        return "進捗";
      }
      return "";
    },
    cellRenderer: MergedChildCellRenderer,
    onCellClicked: (params: any) => {
      if (
        params.event &&
        params.event.target &&
        (params.event.target as HTMLElement).classList.contains(
          "merged-child-link"
        )
      ) {
        params.event.stopPropagation();
      }
    },
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fa fa-sitemap", "fa fa-spinner"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  product_name: {
    headerName: "商品名",
    field: "product_name",
    width: 150,
    sortable: true,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-boxes"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },

  mach_code: {
    headerName: "機械作業コード",
    field: "mach_code",
    width: 100,
    sortable: true,
    hide: false,
  },

  edited_schedule_time_start: {
    headerName: "編集(開始時刻)",
    field: "edited_schedule_time_start",
    sortable: true,
    width: 200,
    editable: true,
    filter: "agDateColumnFilter",
    valueFormatter: GridUtils.datetimeCellValueFormatter,
    cellEditor: InputDatetimeCellEditor,
    hide: true,
  },
  edited_schedule_time_end: {
    headerName: "編集(終了時刻)",
    field: "edited_schedule_time_end",
    sortable: true,
    width: 200,
    editable: true,
    filter: "agDateColumnFilter",
    valueFormatter: GridUtils.datetimeCellValueFormatter,
    cellEditor: InputDatetimeCellEditor,
    hide: true,
  },
  proc_order: {
    headerName: "加工順",
    field: "proc_order",
    sortable: true,
    width: 120,
    editable: true,
    filter: "agSetColumnFilter",
    comparator: GridUtils.numericComparatorWithNullsLast,
    hide: true,
  },
  is_fin_proc_schedule_complete: {
    headerName: "計画済",
    field: "is_fin_proc_schedule_complete",
    width: 80,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#F9C748", "white"),
    hide: true,
  },
  po_data_num: {
    headerName: "注文NO",
    field: "po_data_num",
    width: 120,
    cellStyle: {
      fontSize: "14px",
    },
    sortable: true,
    filter: "agTextColumnFilter",
  },
  po_desc_num: {
    headerName: "受注ＮＯ",
    field: "po_desc_num",
    width: 170,
    cellStyle: {
      fontSize: "14px",
    },
    filter: "agTextColumnFilter",
  },
  simulated_proc_time: {
    headerName: "加工時間",
    field: "simulated_proc_time",
    width: 80,
    sortable: true,
  },
  thickness: {
    headerName: "板厚",
    field: "thickness",
    width: 80,
    sortable: true,
  },
  has_soak: {
    headerName: "ソ",
    field: "has_soak",
    width: 80,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#F9C748", "white"),
    filter: "agSetColumnFilter",
  },
  has_lam: {
    headerName: "合",
    field: "has_lam",
    width: 80,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#F9C748", "white"),
    filter: "agSetColumnFilter",
  },
  has_ig: {
    headerName: "複",
    field: "has_ig",
    width: 80,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#F9C748", "white"),
    filter: "agSetColumnFilter",
  },
  has_film: {
    headerName: "フ",
    field: "has_film",
    width: 80,
    sortable: true,
    cellStyle: GridUtils.highlightIfOneWithColor("#F9C748", "white"),
    filter: "agSetColumnFilter",
  },
  cost_limit: {
    headerName: "原価リミット",
    field: "cost_limit",
    width: 100,
  },
  recipe_id: {
    headerName: "レシピ",
    field: "recipe_id",
    width: 80,
  },
  recipe_name: {
    headerName: "レシピ名",
    field: "recipe_name",
    width: 180,
  },
  tempering_cycle_time: {
    headerName: "強CT",
    field: "tempering_cycle_time",
    width: 100,
  },
  evaluate_fn_string: {
    headerName: "レシピ関数",
    field: "evaluate_fn_string",
    width: 100,
    hide: true,
  },
  order_data_created_at: {
    headerName: "データ作成時刻",
    field: "order_data_created_at",
    width: 200,
    filter: "agTextColumnFilter",
    sortable: true,
  },
  interlayer: {
    headerName: "中間膜",
    field: "interlayer",
    width: 200,
    filter: "agTextColumnFilter",
    sortable: true,
  },
  film_type: {
    headerName: "フィルム",
    field: "film_type",
    width: 140,
    filter: "agTextColumnFilter",
    sortable: true,
  },
  marks: {
    headerName: "Marks",
    field: "marks",
    width: 200,
    cellRenderer: marksCellRenderer,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-tags"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  edge_finishes: {
    headerName: "小口加工",
    field: "edge_finishes",
    width: 200,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-tags"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  branched_parent_po_desc_num: {
    headerName: "親受注ＮＯ",
    field: "branched_parent_po_desc_num",
    width: 200,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-tags"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
    valueGetter: (params: any) => {
      return params.data.branched_parent_po_desc_num || params.data.po_desc_num;
    },
    filter: "agSetColumnFilter",
  },
  ink_type: {
    headerName: "印刷種別",
    field: "ink_type",
    width: 200,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-paint-brush"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  latest_feasible_start_date: {
    headerName: "基準日",
    field: "latest_feasible_start_date",
    width: 120,
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-calendar"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  tempering_schedule_start_time: {
    headerName: "強-sched.",
    field: "tempering_schedule_start_time",
    width: 120,
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-calendar"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  laminating_schedule_start_time: {
    headerName: "合-sched.",
    field: "laminating_schedule_start_time",
    width: 120,
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-calendar"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
  insulating_schedule_start_time: {
    headerName: "複-sched.",
    field: "insulating_schedule_start_time",
    width: 120,
    ...dateSettings,
    headerComponent: IconHeaderComponent,
    headerComponentParams: {
      iconClasses: ["fas fa-calendar"],
      iconColor: "#1773A1",
      showHeaderName: true,
    },
  },
};

// 基本レイアウト
const baseLayout: GetPfmLayout = {
  plant_code: "",
  proc_code: "",
  mach_code: "",
  layout_id: 0,
  layout_name: "デフォルト",
  columns: [
    // ステータス
    baseColumns.proc_status,
    baseColumns.ready,
    baseColumns.po_data_num,
    baseColumns.po_desc_num,
    // baseColumns.schedule_start_time,
    baseColumns.latest_feasible_start_date,
    baseColumns.completion_due_date,
    baseColumns.ship_date,
    baseColumns.sheet_type,
    baseColumns.po_width,
    baseColumns.po_height,
    baseColumns.current_qty,
    baseColumns.film_type,
    baseColumns.destin_name,
    baseColumns.product_name,
    baseColumns.project_name,
    baseColumns.marks,
    // 基本情報
    baseColumns.po_width,
    baseColumns.po_height,
    // option
    baseColumns.edge_finishes,
    baseColumns.following_mach_name,
    baseColumns.remaining_procs,
    baseColumns.interlayer,
    // 出荷情報
    baseColumns.arrival_date,
    // 顧客情報
    baseColumns.customer_name,
    baseColumns.edited_schedule_time_start,
    baseColumns.edited_schedule_time_end,
    baseColumns.thickness,
    baseColumns.mach_name,
    baseColumns.proc_order,
    baseColumns.is_fin_proc_schedule_complete,
    baseColumns.proc_width,
    baseColumns.proc_height,
  ],
};

// カラムの修正を指定するための型
type ColumnModification = {
  action: "add" | "remove" | "move";
  column?: ColDef;
  field?: string;
  afterField?: string;
};

// レイアウト生成関数
function generateLayout(
  plantCode: string,
  procCode: string,
  machCode: string,
  layoutId: number,
  layoutName: string,
  columnModifications: ColumnModification[]
): GetPfmLayout {
  const newLayout = {
    ...baseLayout,
    plant_code: plantCode,
    proc_code: procCode,
    mach_code: machCode,
    layout_id: layoutId,
    layout_name: layoutName,
    columns: [...baseLayout.columns], // コピーを作成
  };

  columnModifications.forEach((mod) => {
    switch (mod.action) {
      case "add":
        if (mod.column) {
          if (mod.afterField) {
            const index = newLayout.columns.findIndex(
              (col) => col.field === mod.afterField
            );
            if (index !== -1) {
              newLayout.columns.splice(index + 1, 0, mod.column);
            } else {
              console.warn(
                `afterField "${mod.afterField}" が見つかりません。末尾に追加します。`
              );
              newLayout.columns.push(mod.column);
            }
          } else {
            newLayout.columns.push(mod.column);
          }
        }
        break;
      case "remove":
        if (mod.field) {
          const originalLength = newLayout.columns.length;
          newLayout.columns = newLayout.columns.filter(
            (col) => col.field !== mod.field
          );
          if (newLayout.columns.length === originalLength) {
            console.warn(
              `field "${mod.field}" が見つかりません。削除できませんでした。`
            );
          }
        }
        break;
      case "move":
        if (mod.field && mod.afterField) {
          const columnToMove = newLayout.columns.find(
            (col) => col.field === mod.field
          );
          if (columnToMove) {
            newLayout.columns = newLayout.columns.filter(
              (col) => col.field !== mod.field
            );
            const newIndex = newLayout.columns.findIndex(
              (col) => col.field === mod.afterField
            );
            if (newIndex !== -1) {
              newLayout.columns.splice(newIndex + 1, 0, columnToMove);
            } else {
              console.warn(
                `afterField "${mod.afterField}" が見つかりません。末尾に移動します。`
              );
              newLayout.columns.push(columnToMove);
            }
          } else {
            console.warn(
              `field "${mod.field}" が見つかりません。移動できませんでした。`
            );
          }
        }
        break;
    }
  });

  return newLayout;
}

// レイアウトの定義
export const DefaultLayouts: GetPfmLayout[] = [
  baseLayout,

  // proc_code: "011"
  // 本社
  {
    plant_code: "01",
    proc_code: "011",
    mach_code: "101",
    layout_id: 101,
    layout_name: "シャトル",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },
  {
    plant_code: "01",
    proc_code: "011",
    mach_code: "103",
    layout_id: 103,
    layout_name: "ガントリー",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },
  {
    plant_code: "01",
    proc_code: "011",
    mach_code: "108",
    layout_id: 108,
    layout_name: "パネルソー",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "01",
    proc_code: "011",
    mach_code: "186",
    layout_id: 186,
    layout_name: "手切り",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "01",
    proc_code: "011",
    mach_code: "105",
    layout_id: 105,
    layout_name: "合せ切断",
    columns: [
      baseColumns.branched_parent_po_desc_num,
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.interlayer,
      baseColumns.marks,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },

  // 関東
  {
    plant_code: "05",
    proc_code: "011",
    mach_code: "501",
    layout_id: 501,
    layout_name: "切断",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },
  {
    plant_code: "05",
    proc_code: "011",
    mach_code: "502",
    layout_id: 502,
    layout_name: "切断",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },
  {
    plant_code: "05",
    proc_code: "011",
    mach_code: "503",
    layout_id: 503,
    layout_name: "切断",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },
  {
    plant_code: "05",
    proc_code: "011",
    mach_code: "505",
    layout_id: 505,
    layout_name: "切断",
    columns: [
      baseColumns.branched_parent_po_desc_num,
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.interlayer,
      baseColumns.marks,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
      // ↑ ここまで検討済み
    ],
  },
  {
    plant_code: "05",
    proc_code: "011",
    mach_code: "586",
    layout_id: 586,
    layout_name: "切断1",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },

  // 原寸受入
  // 本社
  {
    plant_code: "05",
    proc_code: "012",
    mach_code: "570",
    layout_id: 570,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "01",
    proc_code: "012",
    mach_code: "170",
    layout_id: 170,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "01",
    proc_code: "012",
    mach_code: "171",
    layout_id: 171,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "01",
    proc_code: "012",
    mach_code: "172",
    layout_id: 172,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "05",
    proc_code: "012",
    mach_code: "572",
    layout_id: 572,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "01",
    proc_code: "012",
    mach_code: "570",
    layout_id: 570,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "05",
    proc_code: "012",
    mach_code: "571",
    layout_id: 571,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },
  {
    plant_code: "05",
    proc_code: "012",
    mach_code: "572",
    layout_id: 572,
    layout_name: "原寸受入",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.sheet_type,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.following_mach_name,
      baseColumns.destin_name,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.product_name,
      baseColumns.arrival_date,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.remaining_procs,
    ],
  },

  // proc_code: "031"
  {
    plant_code: "01",
    proc_code: "031",
    mach_code: "120",
    layout_id: 120,
    layout_name: "鈴木Vカット",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  // proc_code: "051"
  // 本社
  {
    plant_code: "01",
    proc_code: "051",
    mach_code: "113",
    layout_id: 113,
    layout_name: "網",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "051",
    mach_code: "114",
    layout_id: 114,
    layout_name: "ROCK",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "051",
    mach_code: "118",
    layout_id: 118,
    layout_name: "Bライン",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "061",
    mach_code: "121",
    layout_id: 121,
    layout_name: "オートライン",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "061",
    mach_code: "122",
    layout_id: 122,
    layout_name: "マスター333",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "061",
    mach_code: "124",
    layout_id: 124,
    layout_name: "SB10",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "061",
    mach_code: "126",
    layout_id: 126,
    layout_name: "VERTMAX",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "061",
    mach_code: "188",
    layout_id: 188,
    layout_name: "手加工",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "061",
    mach_code: "189",
    layout_id: 189,
    layout_name: "R取機",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "071",
    mach_code: "125",
    layout_id: 125,
    layout_name: "R取機",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "071",
    mach_code: "127",
    layout_id: 127,
    layout_name: "スキャッティ4軸",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "091",
    mach_code: "130",
    layout_id: 130,
    layout_name: "引手堀機",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "101",
    mach_code: "131",
    layout_id: 131,
    layout_name: "中型洗浄機",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "121",
    mach_code: "136",
    layout_id: 136,
    layout_name: "東海中型1",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.ink_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "121",
    mach_code: "137",
    layout_id: 137,
    layout_name: "東海中型2",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.ink_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "121",
    mach_code: "139",
    layout_id: 139,
    layout_name: "印刷1F",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.ink_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  // 関東
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "512",
    layout_id: 512,
    layout_name: "キアラ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "513",
    layout_id: 513,
    layout_name: "網",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "514",
    layout_id: 514,
    layout_name: "ROCK",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "515",
    layout_id: 515,
    layout_name: "EG",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "517",
    layout_id: 517,
    layout_name: "ジャロジー",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "518",
    layout_id: 518,
    layout_name: "W",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "519",
    layout_id: 519,
    layout_name: "ブセッティ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "521",
    layout_id: 521,
    layout_name: "GZM",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.product_name,
      baseColumns.destin_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.customer_name,
      baseColumns.project_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "523",
    layout_id: 523,
    layout_name: "FPS15",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.edge_finishes,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "557",
    layout_id: 557,
    layout_name: "NI",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "051",
    mach_code: "597",
    layout_id: 587,
    layout_name: "手加工",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "061",
    mach_code: "522",
    layout_id: 522,
    layout_name: "インターマック",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "071",
    mach_code: "525",
    layout_id: 525,
    layout_name: "フォルベット小型",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "071",
    mach_code: "527",
    layout_id: 527,
    layout_name: "スキャッティ4軸",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "071",
    mach_code: "528",
    layout_id: 528,
    layout_name: "フォルベット大型",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "081",
    mach_code: "529",
    layout_id: 529,
    layout_name: "切込機",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "091",
    mach_code: "530",
    layout_id: 530,
    layout_name: "引手",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "101",
    mach_code: "531",
    layout_id: 531,
    layout_name: "洗浄機",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "121",
    mach_code: "539",
    layout_id: 539,
    layout_name: "東海工業",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "121",
    mach_code: "541",
    layout_id: 541,
    layout_name: "コートテック",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  // 本社
  {
    plant_code: "01",
    proc_code: "161",
    mach_code: "145",
    layout_id: 145,
    layout_name: "RC",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "161",
    mach_code: "146",
    layout_id: 146,
    layout_name: "FC",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "162",
    mach_code: "148",
    layout_id: 148,
    layout_name: "ソーク",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "162",
    mach_code: "150",
    layout_id: 150,
    layout_name: "ソーク",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  // 関東
  {
    plant_code: "05",
    proc_code: "161",
    mach_code: "545",
    layout_id: 545,
    layout_name: "RC",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "161",
    mach_code: "546",
    layout_id: 546,
    layout_name: "FC",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "162",
    mach_code: "548",
    layout_id: 548,
    layout_name: "強化ソーク1号機",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "162",
    mach_code: "565",
    layout_id: 565,
    layout_name: "強化ソーク2号機",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "184",
    mach_code: "555",
    layout_id: 555,
    layout_name: "ソーク",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "194",
    mach_code: "151",
    layout_id: 151,
    layout_name: "タム",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.ready,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "194",
    mach_code: "154",
    layout_id: 154,
    layout_name: "イアヌア",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.ready,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "194",
    mach_code: "155",
    layout_id: 155,
    layout_name: "ソーク",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.ready,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "186",
    mach_code: "551",
    layout_id: 551,
    layout_name: "ショルツ大",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.ready,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "186",
    mach_code: "554",
    layout_id: 554,
    layout_name: "ショルツ小",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "186",
    mach_code: "555",
    layout_id: 555,
    layout_name: "ソーク",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "186",
    mach_code: "568",
    layout_id: 568,
    layout_name: "装飾合せ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "18A",
    mach_code: "567",
    layout_id: 567,
    layout_name: "自然光",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "18A",
    mach_code: "592",
    layout_id: 592,
    layout_name: "ソーラーシミュレータ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },

  {
    plant_code: "01",
    proc_code: "191",
    mach_code: "152",
    layout_id: 152,
    layout_name: "フィルム切断機",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },

  {
    plant_code: "05",
    proc_code: "191",
    mach_code: "552",
    layout_id: 552,
    layout_name: "フィルム切断機",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },

  // 合せ積層 proc_code: 193
  {
    plant_code: "01",
    proc_code: "193",
    mach_code: "153",
    layout_id: 153,
    layout_name: "合せ積層",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.merged_children,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "193",
    mach_code: "553",
    layout_id: 553,
    layout_name: "合せ積層",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.merged_children,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "193",
    mach_code: "575",
    layout_id: 575,
    layout_name: "装飾合せ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "194",
    mach_code: "551",
    layout_id: 551,
    layout_name: "ショルツ大",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "194",
    mach_code: "554",
    layout_id: 554,
    layout_name: "ショルツ小",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "194",
    mach_code: "555",
    layout_id: 555,
    layout_name: "ソーク",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "194",
    mach_code: "568",
    layout_id: 568,
    layout_name: "装飾",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },

  {
    plant_code: "01",
    proc_code: "213",
    mach_code: "159",
    layout_id: 159,
    layout_name: "リーゼック",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "213",
    mach_code: "169",
    layout_id: 169,
    layout_name: "曲げ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "214",
    mach_code: "161",
    layout_id: 161,
    layout_name: "カンソーン",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "221",
    mach_code: "163",
    layout_id: 163,
    layout_name: "水平型ラミネータ",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.film_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "221",
    mach_code: "190",
    layout_id: 190,
    layout_name: "手貼り",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.film_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "213",
    mach_code: "559",
    layout_id: 559,
    layout_name: "複層積層",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "214",
    mach_code: "561",
    layout_id: 561,
    layout_name: "小型ソーク炉",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.interlayer,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.marks,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "221",
    mach_code: "563",
    layout_id: 563,
    layout_name: "フィルム貼",
    columns: [
      baseColumns.proc_status,
      baseColumns.ready,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.po_width,
      baseColumns.po_height,
      baseColumns.current_qty,
      baseColumns.film_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "221",
    mach_code: "564",
    layout_id: 564,
    layout_name: "フィルム貼",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.film_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "221",
    mach_code: "590",
    layout_id: 590,
    layout_name: "手貼",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.film_type,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },

  // 梱包
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "193",
    layout_id: 193,
    layout_name: "倉庫梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "193",
    layout_id: 193,
    layout_name: "倉庫梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "194",
    layout_id: 194,
    layout_name: "フィルム貼り",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "195",
    layout_id: 195,
    layout_name: "生板",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "196",
    layout_id: 196,
    layout_name: "強化",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "197",
    layout_id: 197,
    layout_name: "ソーラー",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "198",
    layout_id: 198,
    layout_name: "合せ",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "01",
    proc_code: "891",
    mach_code: "199",
    layout_id: 199,
    layout_name: "複層",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },

  {
    plant_code: "05",
    proc_code: "891",
    mach_code: "595",
    layout_id: 595,
    layout_name: "梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "891",
    mach_code: "596",
    layout_id: 596,
    layout_name: "梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "891",
    mach_code: "597",
    layout_id: 597,
    layout_name: "梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "891",
    mach_code: "598",
    layout_id: 598,
    layout_name: "梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
  {
    plant_code: "05",
    proc_code: "891",
    mach_code: "599",
    layout_id: 599,
    layout_name: "梱包",
    columns: [
      baseColumns.proc_status,
      baseColumns.po_data_num,
      baseColumns.po_desc_num,
      // baseColumns.schedule_start_time,
      baseColumns.latest_feasible_start_date,
      baseColumns.completion_due_date,
      baseColumns.ship_date,
      baseColumns.tempering_schedule_start_time,
      baseColumns.laminating_schedule_start_time,
      baseColumns.insulating_schedule_start_time,
      baseColumns.destin_name,
      baseColumns.product_name,
      baseColumns.proc_width,
      baseColumns.proc_height,
      baseColumns.current_qty,
      baseColumns.remaining_procs,
      baseColumns.project_name,
      baseColumns.customer_name,
      baseColumns.arrival_date,
      baseColumns.edited_schedule_time_start,
      baseColumns.edited_schedule_time_end,
      baseColumns.thickness,
      baseColumns.mach_name,
      baseColumns.following_mach_name,
    ],
  },
];

// 条件に基づいてレイアウトを取得する関数
export function getLayoutForMachine(
  plantCode: string,
  procCode: string,
  machCode: string
): GetPfmLayout {
  const specificLayout = DefaultLayouts.find(
    (layout) =>
      layout.plant_code === plantCode &&
      layout.proc_code === procCode &&
      layout.mach_code === machCode
  );
  return specificLayout || baseLayout;
}
